import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAppLocale } from '@/hooks/useAppLocale'
import { formatDateTime } from '@/utils/date'
import { Mail, Phone, Plus, Store, User } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetVendorItems, useGetVendorProducts, useGetVendorPurchaseHistories } from '../hooks/useVendors'
import type { Vendor } from '../types/vendor'
import { PurchaseHistoryDialog } from './PurchaseHistoryDialog'
import { VendorProductDialog } from './VendorProductDialog'

export function VendorDetailDialog({
  vendor,
  open,
  onOpenChange,
}: {
  vendor: Vendor | null
  open: boolean
  onOpenChange: (o: boolean) => void
}) {
  const { t } = useTranslation()
  const locale = useAppLocale()
  const vendorId = vendor?.id
  const [productDialogOpen, setProductDialogOpen] = useState(false)
  const [purchaseDialogOpen, setPurchaseDialogOpen] = useState(false)
  const hasVendorItems = (vendor?.itemCount ?? 0) > 0
  const {
    data: vendorProducts = [],
    isLoading: isLoadingVendorProducts,
    isError: isVendorProductsError,
  } = useGetVendorProducts(vendorId, open)
  const {
    data: vendorPurchaseHistories = [],
    isLoading: isLoadingVendorPurchaseHistories,
    isError: isVendorPurchaseHistoriesError,
  } = useGetVendorPurchaseHistories(vendorId, open)
  const {
    data: vendorItemsData,
    isLoading: isLoadingVendorItems,
    isError: isVendorItemsError,
  } = useGetVendorItems(vendorId, open && hasVendorItems)

  if (!vendor) return null

  const vendorItems = vendorItemsData?.items ?? []

  const formatMoney = (value: string | null) => {
    if (!value) return '-'

    const numericValue = Number(value)

    return Number.isFinite(numericValue) ? numericValue.toLocaleString(locale) : value
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="vendor-detail-dialog" className="border-border bg-card text-foreground sm:max-w-xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="vendor-detail-name" className="flex items-center gap-2 text-foreground">
            <Store className="size-4 text-primary" />
            {vendor.name}
          </DialogTitle>
          <DialogDescription className="sr-only">{t('vendors.detailDescription')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4 pt-2">
          <div className="flex flex-col gap-2 rounded-lg border border-border/30 bg-secondary/20 p-3">
            <div className="flex items-center gap-2 text-xs">
              <User className="size-3 text-muted-foreground" />
              <span className="text-muted-foreground">{t('vendors.contact')}:</span>
              <span data-testid="vendor-detail-contact" className="font-medium text-foreground">{vendor.contactName || '-'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Phone className="size-3 text-muted-foreground" />
              <span className="text-muted-foreground">{t('vendors.phone')}:</span>
              <span data-testid="vendor-detail-phone" className="text-foreground">{vendor.phone || '-'}</span>
            </div>
            <div className="flex items-center gap-2 text-xs">
              <Mail className="size-3 text-muted-foreground" />
              <span className="text-muted-foreground">{t('vendors.email')}:</span>
              <span data-testid="vendor-detail-email" className="text-foreground">{vendor.email || '-'}</span>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">
                {t('vendors.availableProducts')} ({vendorProducts.length})
              </span>
              <Button
                data-testid="vendor-product-add-button"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setProductDialogOpen(true)}
              >
                <Plus className="mr-1 size-3" />
                {t('vendors.addProduct')}
              </Button>
            </div>
            {isLoadingVendorProducts ? (
              <p className="text-xs text-muted-foreground py-2">{t('common.loading')}</p>
            ) : isVendorProductsError ? (
              <p className="text-xs text-destructive py-2">{t('vendors.loadProductsError')}</p>
            ) : vendorProducts.length > 0 ? (
              <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {vendorProducts.map(vp => {
                  const productName = vp.product.name || vp.product.categoryName || '-'
                  const productDescription = [vp.product.brand, vp.product.model, vp.product.categoryName]
                    .filter(Boolean)
                    .join(' / ')

                  return (
                    <div key={vp.id} className="rounded border border-border/30 bg-secondary/30 p-2 text-xs">
                      <div className="flex items-center justify-between">
                        {vp.catalogUrl ? (
                          <a
                            data-testid={`vendor-product-link-${vp.id}`}
                            href={vp.catalogUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-medium text-foreground underline-offset-2 hover:text-primary hover:underline"
                          >
                            {productName}
                          </a>
                        ) : (
                          <span className="font-medium text-foreground">{productName}</span>
                        )}
                        <span className="text-primary">
                          {formatMoney(vp.price)} {t('vendors.baht')}
                        </span>
                      </div>
                      {productDescription && <p className="text-muted-foreground">{productDescription}</p>}
                      <p className="text-muted-foreground">{vp.spec}</p>
                      <p className="text-[10px] text-muted-foreground">
                        {t('vendors.warranty')}: {vp.warranty || '-'} | {t('vendors.maintenance')}:{' '}
                        {formatMoney(vp.maintenancePrice)} {t('vendors.bahtPerYear')}
                      </p>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">{t('vendors.noProducts')}</p>
            )}
          </div>

          <div className="flex flex-col gap-2" data-testid="vendor-detail-purchase-history">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground">
                {t('vendors.purchaseHistory')} ({vendorPurchaseHistories.length})
              </span>
              <Button
                data-testid="purchase-history-add-button"
                size="sm"
                className="h-8 text-xs"
                onClick={() => setPurchaseDialogOpen(true)}
              >
                <Plus className="mr-1 size-3" />
                {t('vendors.addPurchase')}
              </Button>
            </div>
            {isLoadingVendorPurchaseHistories ? (
              <p className="text-xs text-muted-foreground py-2">{t('common.loading')}</p>
            ) : isVendorPurchaseHistoriesError ? (
              <p className="text-xs text-destructive py-2">{t('vendors.loadPurchaseHistoryError')}</p>
            ) : vendorPurchaseHistories.length > 0 ? (
              <div className="flex max-h-48 flex-col gap-1 overflow-y-auto">
                {vendorPurchaseHistories.map(ph => {
                  const totalPrice = Number(ph.price) * Number(ph.quantity)

                  return (
                    <div key={ph.id} data-testid="vendor-detail-purchase-entry" className="rounded border border-border/30 bg-secondary/30 p-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-foreground">
                          {ph.productName} x {ph.quantity}
                        </span>
                        <span className="text-primary">
                          {Number.isFinite(totalPrice) ? totalPrice.toLocaleString(locale) : formatMoney(ph.price)}{' '}
                          {t('vendors.baht')}
                        </span>
                      </div>
                      <p className="text-[10px] text-muted-foreground">
                        {t('common.date')}: {formatDateTime(ph.date, locale)} | {t('vendors.price')}:{' '}
                        {formatMoney(ph.price)} {t('vendors.baht')}
                      </p>
                    </div>
                  )
                })}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">{t('vendors.noPurchaseHistory')}</p>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-foreground">
              {t('vendors.vendorItems')} ({vendorItems.length})
            </span>
            {isLoadingVendorItems ? (
              <p className="text-xs text-muted-foreground py-2">{t('common.loading')}</p>
            ) : isVendorItemsError ? (
              <p className="text-xs text-destructive py-2">{t('vendors.loadItemsError')}</p>
            ) : vendorItems.length > 0 ? (
              <div className="flex flex-wrap gap-1">
                {vendorItems.slice(0, 10).map(item => {
                  return (
                    <Badge key={item.id} variant="outline" className="text-[10px]">
                      {item.productName} - {item.serialNumber}
                    </Badge>
                  )
                })}
                {vendorItems.length > 10 && (
                  <Badge variant="outline" className="text-[10px]">
                    +{vendorItems.length - 10} {t('common.equipmentUnit')}
                  </Badge>
                )}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground py-2">{t('vendors.noItems')}</p>
            )}
          </div>
        </div>
      </DialogContent>
      </Dialog>
      <VendorProductDialog vendor={vendor} open={productDialogOpen} onOpenChange={setProductDialogOpen} />
      <PurchaseHistoryDialog vendor={vendor} open={purchaseDialogOpen} onOpenChange={setPurchaseDialogOpen} />
    </>
  )
}
